import React, { useState } from "react";
import { X, User, MapPin, Package, ShoppingCart, DollarSign, Settings, Save, Plus } from "lucide-react";

interface AddOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (orderData: any) => Promise<void>;
  accounts: Array<{ id: number; username: string }>;
  sources: Array<{ id: number; name: string }>;
  products: Array<{ id: number; name: string; price: number; currency: string }>;
  restrictedCountry?: string; // For country-post-managers, restrict to their country
  isCountryRestricted?: boolean; // Whether the user is restricted to a specific country
  currentUser?: {
    username: string;
    email: string;
    role: string;
    countries?: string[];
    permissions?: string[];
    profile?: {
      firstName: string;
      lastName: string;
      phone: string;
      department: string;
    };
  };
}

const AddOrderModal: React.FC<AddOrderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  accounts,
  sources,
  products,
  restrictedCountry,
  isCountryRestricted = false,
  currentUser,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  
  // Get initial country based on restriction
  const getInitialCountry = () => {
    if (isCountryRestricted && restrictedCountry) {
      return restrictedCountry.toLowerCase();
    }
    return "gr";
  };
  
  const [formData, setFormData] = useState({
    // Customer Information
    name: "",
    phone: "",
    email: "",
    gender: "0",
    
    // Address Information
    country: getInitialCountry(),
    city: "",
    area: "",
    street: "",
    addr: "",
    index: "",
    
    // Product Information
    offer: "",
    offername: "",
    count: "1",
    price: "",
    currency: "eur",
    
    // Account/Source
    user: "",
    wm: "",
    
    // Status
    status: "6", // Packaging
    stage: "0",
    
    // Source indicator
    source: "manual", // manual or altercpa
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = "Customer name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.offer) newErrors.offer = "Product is required";
    if (!formData.offername.trim()) newErrors.offername = "Product name is required";
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = "Valid price is required";
    }
    if (!formData.user) newErrors.user = "Account is required";
    if (!formData.wm) newErrors.wm = "Source is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProductChange = (offerId: string) => {
    const product = products.find((p) => p.id.toString() === offerId);
    if (product) {
      setFormData((prev) => ({
        ...prev,
        offer: offerId,
        offername: product.name,
        price: product.price.toString(),
        currency: product.currency.toLowerCase(),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        offer: offerId,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const orderData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || "",
        gender: parseInt(formData.gender),
        
        country: formData.country,
        city: formData.city,
        area: formData.area || "N/A",
        street: formData.street || "",
        addr: formData.addr || "",
        index: formData.index || "",
        
        offer: parseInt(formData.offer),
        offername: formData.offername,
        count: parseInt(formData.count),
        price: parseFloat(formData.price),
        currency: formData.currency,
        
        user: parseInt(formData.user),
        wm: parseInt(formData.wm),
        
        status: parseInt(formData.status),
        stage: parseInt(formData.stage),
        
        source: formData.source, // manual or altercpa
        
        // Additional required fields
        delivery: 1,
        discount: 0,
        base: 0,
        paid: 0,
        done: 0,
        phase: 3,
        reason: 0,
        hold: 0,
        check: 0,
        
        time: Math.floor(Date.now() / 1000),
      };

      await onSave(orderData);
      onClose();
      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        gender: "0",
        country: getInitialCountry(),
        city: "",
        area: "",
        street: "",
        addr: "",
        index: "",
        offer: "",
        offername: "",
        count: "1",
        price: "",
        currency: "eur",
        user: "",
        wm: "",
        status: "6",
        stage: "0",
        source: "manual",
      });
      setErrors({});
    } catch (error) {
      console.error("Error saving order:", error);
      alert("Failed to save order. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Plus className="w-5 h-5 mr-2 text-[#264a3e]" />
              Add New Order
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* User Info Banner for Country Post Managers */}
          {isCountryRestricted && currentUser && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <div className="flex items-start">
                <User className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    {currentUser.profile?.firstName && currentUser.profile?.lastName
                      ? `${currentUser.profile.firstName} ${currentUser.profile.lastName}`.trim()
                      : currentUser.username}
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    {currentUser.email} • {currentUser.role.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                  <div className="flex items-center mt-2 space-x-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-800">
                      Creating order for: {(restrictedCountry || "").toUpperCase()}
                    </span>
                    {currentUser.countries && currentUser.countries.length > 0 && (
                      <span className="text-xs text-blue-600">
                        (Your assigned {currentUser.countries.length === 1 ? "country" : "countries"}: {currentUser.countries.join(", ")})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Information */}
            <div className="md:col-span-2">
              <div className="flex items-center mb-3">
                <User className="w-5 h-5 mr-2 text-[#264a3e]" />
                <h4 className="text-sm font-semibold text-gray-900">Customer Information</h4>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              >
                <option value="0">Not Specified</option>
                <option value="1">Male</option>
                <option value="2">Female</option>
              </select>
            </div>

            {/* Address Information */}
            <div className="md:col-span-2 mt-4">
              <div className="flex items-center mb-3">
                <MapPin className="w-5 h-5 mr-2 text-[#264a3e]" />
                <h4 className="text-sm font-semibold text-gray-900">Address Information</h4>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
                {isCountryRestricted && (
                  <span className="ml-2 text-xs text-blue-600">(Restricted to your country)</span>
                )}
              </label>
              <select
                value={formData.country}
                onChange={(e) => handleChange("country", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.country ? "border-red-500" : "border-gray-300"
                } ${isCountryRestricted ? "bg-gray-100 cursor-not-allowed" : ""}`}
                required
                disabled={isCountryRestricted}
              >
                <option value="gr">Greece (GR)</option>
                <option value="rs">Serbia (RS)</option>
                <option value="mk">North Macedonia (MK)</option>
                <option value="bg">Bulgaria (BG)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.city ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Area/Region</label>
              <input
                type="text"
                value={formData.area}
                onChange={(e) => handleChange("area", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => handleChange("street", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Number</label>
              <input
                type="text"
                value={formData.addr}
                onChange={(e) => handleChange("addr", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zipcode</label>
              <input
                type="text"
                value={formData.index}
                onChange={(e) => handleChange("index", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
            </div>

            {/* Product Information */}
            <div className="md:col-span-2 mt-4">
              <div className="flex items-center mb-3">
                <ShoppingCart className="w-5 h-5 mr-2 text-[#264a3e]" />
                <h4 className="text-sm font-semibold text-gray-900">Product Information</h4>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.offer}
                onChange={(e) => handleProductChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.offer ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="">Select Product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} - {product.price} {product.currency.toUpperCase()}
                  </option>
                ))}
              </select>
              {errors.offer && <p className="text-red-500 text-xs mt-1">{errors.offer}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.offername}
                onChange={(e) => handleChange("offername", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.offername ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.offername && <p className="text-red-500 text-xs mt-1">{errors.offername}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                value={formData.count}
                onChange={(e) => handleChange("count", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleChange("price", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.price ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => handleChange("currency", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              >
                <option value="eur">EUR</option>
                <option value="usd">USD</option>
                <option value="mkd">MKD</option>
                <option value="rsd">RSD</option>
                <option value="bgn">BGN</option>
              </select>
            </div>

            {/* Account/Source */}
            <div className="md:col-span-2 mt-4">
              <div className="flex items-center mb-3">
                <Settings className="w-5 h-5 mr-2 text-[#264a3e]" />
                <h4 className="text-sm font-semibold text-gray-900">Account & Source</h4>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.user}
                onChange={(e) => handleChange("user", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.user ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="">Select Account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    #{account.id} - {account.username}
                  </option>
                ))}
              </select>
              {errors.user && <p className="text-red-500 text-xs mt-1">{errors.user}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.wm}
                onChange={(e) => handleChange("wm", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] ${
                  errors.wm ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="">Select Source</option>
                {sources.map((source) => (
                  <option key={source.id} value={source.id}>
                    #{source.id} - {source.name}
                  </option>
                ))}
              </select>
              {errors.wm && <p className="text-red-500 text-xs mt-1">{errors.wm}</p>}
            </div>

            <div className="md:col-span-2 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Source</label>
              <select
                value={formData.source}
                onChange={(e) => handleChange("source", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
              >
                <option value="manual">Manual Entry (Outbound)</option>
                <option value="altercpa">AlterCPA (Inbound)</option>
              </select>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Save className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddOrderModal;

